import React, { Fragment, Component, PureComponent } from "react";
import styled, { keyframes, css } from "styled-components";
import Observer from "@researchgate/react-intersection-observer";
import { basename } from "path";
import { RandomDelayedLetter } from "../../containers/Transitions";
import {
  LoadAdaptedImage,
  ProgressiveImage,
  findOptimalImage
} from "../../containers/ProgressiveHelpers";
import {
  Stretched,
  AbsoluteStretched,
  Centered,
  Logo,
  LogoStyle
} from "../../containers/Layout";
import PreserveDimensions from "../../containers/PreserveDimensions";

const HeroContainer = styled(PreserveDimensions)`
  overflow: hidden;
  position: relative;
  height: 100vh;
  width: 100%;
`;

const fadeout = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const fadein = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: url(${props => props.src});
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  animation: ${props =>
    props.transitioning &&
    css`
      ${fadeout} 1s forwards;
    `};

  @media (pointer: coarse) and (hover: none) {
    background-attachment: scroll;
  }
`;

const Placeholder = styled(Background)`
  filter: blur(15px);
  transform: scale(1.1);
`;

const SlideshowStyle = styled.div`
  animation: ${fadein} 1.5s forwards;
`;

class Slideshow extends Component {
  state = {};

  constructor(props) {
    super(props);

    this.element = typeof document !== "undefined" && document.documentElement;
  }

  static getKey = (image, images) => image + "--" + images.indexOf(image);

  static getDerivedStateFromProps = (nextProps, prevState) => {
    if (nextProps.images === prevState.images) {
      return prevState;
    }

    const state = {
      active:
        prevState.active !== undefined ? prevState.active : nextProps.images[0],
      nextActive:
        nextProps.images.length >= 1
          ? nextProps.images[1]
          : nextProps.images[0],
      images: nextProps.images
    };

    return {
      ...state,
      key: Slideshow.getKey(state.active, state.images),
      nextKey: Slideshow.getKey(state.nextActive, state.images)
    };
  };

  componentDidMount = () => {
    this.interval = setInterval(this.triggerTransition, 5000);
  };

  componentWillUnmount = () => {
    clearInterval(this.interval);
  };

  triggerTransition = () => {
    this.setState({
      trigger: true
    });
  };

  next = () => {
    const nextActiveIndex = this.state.images.indexOf(this.state.nextActive);
    const newNextActive =
      nextActiveIndex + 1 < this.state.images.length
        ? this.state.images[nextActiveIndex + 1]
        : this.state.images[0];
    this.setState({
      active: this.state.nextActive,
      nextActive: newNextActive,
      key: this.state.nextKey,
      nextKey: Slideshow.getKey(newNextActive, this.state.images),
      trigger: false
    });
  };

  render = () => {
    return (
      <Fragment>
        <ProgressiveImage
          {...this.state.nextActive}
          key={this.state.nextKey}
          src={findOptimalImage(this.element, this.state.nextActive)}
          children={({ src, isPlaceholder }) =>
            isPlaceholder ? <Placeholder src={src} /> : <Background src={src} />
          }
        />
        <ProgressiveImage
          {...this.state.active}
          key={this.state.key}
          src={findOptimalImage(this.element, this.state.active)}
          children={({ src, isPlaceholder }) =>
            isPlaceholder ? (
              <Placeholder
                src={src}
                onAnimationEnd={this.next}
                transitioning={this.state.trigger}
              />
            ) : (
              <Background
                src={src}
                onAnimationEnd={this.next}
                transitioning={this.state.trigger}
              />
            )
          }
        />
      </Fragment>
    );
  };
}


export const SlightShade = styled(AbsoluteStretched)`
  background: radial-gradient(hsla(0, 0%, 83%, 0.52), rgba(0, 0, 0, 0));
`;

const HeroTitle = styled.h1`
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  font-size: 3.5em;
  color: rgb(115, 126, 255);
  position: relative;
`;

class HeroSection extends Component {
  constructor() {
    super();
    this.state = {
      insideViewport: true
    };
  }

  handleChange = entry =>
    this.setState({
      insideViewport: entry.isIntersecting
    });

  render() {
    const { cms } = this.props;

    return (
      <Observer onChange={this.handleChange}>
        <HeroContainer>
          <Slideshow
            images={cms["hero-section-background-images"].map(filename =>
              require(`../../../public/uploads/${basename(filename)}`)
            )}
          />
          <Centered>
            <SlightShade />
            <HeroTitle>
              <Centered>
                <Logo />
              </Centered>
              {this.state.insideViewport && (
                <Fragment>
                  {Array.from(cms["hero-section-title"]).map(
                    (char, index) =>
                      /\S/.test(char) ? (
                        <RandomDelayedLetter key={index} children={char} />
                      ) : (
                        " "
                      )
                  )}
                </Fragment>
              )}
            </HeroTitle>
          </Centered>
        </HeroContainer>
      </Observer>
    );
  }
}

export default HeroSection;



// WEBPACK FOOTER //
// /opt/build/repo/src/pages/Landing/HeroSection.js